import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';
import { useFnolEligiblePolicies } from './useFnolEligiblePolicies';
import type { FnolEligiblePolicy } from './fnol.types';

export interface CanFileFnolClaim {
  readonly canFileFnolClaim: boolean;
  readonly eligiblePolicies: FnolEligiblePolicy[];
}

/**
 * The one gate every FNOL entry point (the Claims button, the dashboard tile, the routes) shares:
 * web intake is switched on, and the policyholder has at least one policy eligible for it.
 * Keeping the decision in one place means widening or narrowing it only has to happen once.
 *
 * `isInsured` and the feature flag short-circuit the eligibility query when the feature could never
 * show -- an agency or Cowbell user browsing a customer account, or intake switched off -- without
 * skipping the hook call itself, which would break the rules of hooks.
 */
export const useCanFileFnolClaim = (isInsured: boolean): CanFileFnolClaim => {
  const isOpenNewClaimOn = useFeatureIsOn(
    FEATURE_FLAG_KEYS.POLICYHOLDER_OPEN_NEW_CLAIM
  );
  const canOffer = isInsured && isOpenNewClaimOn;
  const { data: eligiblePolicies = [], isError } =
    useFnolEligiblePolicies(canOffer);

  return {
    // A failed eligibility load still offers the entry point: FileNewClaim says the load failed
    // and offers a retry, where hiding the button would leave an eligible policyholder unable to
    // report and not told why.
    canFileFnolClaim:
      canOffer && (eligiblePolicies.length > 0 || Boolean(isError)),
    eligiblePolicies,
  };
};
